/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box, Flex } from '@rebass/grid';
import React from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import styled from 'styled-components';
import messages from '../constants/messages';
import {
    ActivityBookingTypesEnum,
    ActivityPlannerActivityFragment,
    ActivityPlannerTimeslotFragment,
    CartStatusEnum,
} from '../generated/graphql';
import { useCart } from '../hooks/useCart';
import parseDateTime from '../utils/parseDateTime';
import { isUpcoming } from './ActivityPlanner';
import BookButton from './designsystem/BookButton';
import Heading from './designsystem/Heading';
import { IntlShape } from 'react-intl/src/types';

interface Props {
    timeslots: ActivityPlannerTimeslotFragment[];
    selectedDay: Date;
    className?: string;
    activity?: ActivityPlannerActivityFragment;
    setTimeslot: (slot: ActivityPlannerTimeslotFragment | null) => void;
}

const ActivityPlannerTimeslots = ({ timeslots, selectedDay, className, activity, setTimeslot }: Props) => {
    const cart = useCart();
    const intl = useIntl();
    if (!activity) {
        return null;
    }

    const timeslotsAvailable = !!timeslots.length;

    return (
        <Container className={className}>
            <StyledBox className="gt-s">
                <StyledHeading variant="small">
                    <Title className="gt-l">
                        <FormattedMessage
                            defaultMessage="{optionsQuantity} beschikbare opties"
                            values={{ optionsQuantity: timeslots.length }}
                        />
                    </Title>
                    <Title className="lt-l">
                        <FormattedMessage
                            defaultMessage="Beschikbare tijden op {date}"
                            values={{ date: <FormattedDate value={selectedDay} format="FULLWEEKDAYMONTHYEAR" /> }}
                        />
                    </Title>
                </StyledHeading>
                <DateBody>
                    <FormattedDate value={selectedDay} format="WEEKDAYMONTHYEAR" />
                </DateBody>
            </StyledBox>
            <StyledSlotBoxWrapper flex={1}>
                {!timeslotsAvailable && (
                    <Box>
                        <Body>
                            <FormattedMessage defaultMessage="Deze activiteit vindt niet plaats op deze dag" />
                        </Body>
                    </Box>
                )}
                {timeslotsAvailable &&
                    timeslots
                        .sort(
                            (slotA, slotB) =>
                                new Date(slotA.startDateTime).valueOf() - new Date(slotB.startDateTime).valueOf()
                        )
                        .map((slot, index) => {
                            const timeslotIsInCart =
                                cart.cartQuery.data?.cart?.status === CartStatusEnum.Open &&
                                cart.tickets.some(cartTicket => cartTicket.timeslot?.id === slot.id);

                            // if allotment is null it means there's unlimited supply
                            const allotmentAvailable = !!slot.allotment || slot.allotment === null;
                            const timeslotHasExpired = !isUpcoming(slot, true);

                            const startTime = `${new Date(slot?.startDateTime).getHours()}:${new Date(
                                slot?.startDateTime
                            ).getMinutes()}`;
                            const endTime = `${new Date(slot?.endDateTime).getHours()}:${new Date(
                                slot?.endDateTime
                            ).getMinutes()}`;
                            const isFullDay = startTime === '0:0' && endTime === '23:55';

                            const bookButton = renderBookButton(
                                activity,
                                allotmentAvailable,
                                timeslotHasExpired,
                                setTimeslot,
                                slot,
                                timeslotIsInCart,
                                intl
                            );

                            return (
                                <StyledSlotBox key={index}>
                                    <Flex flexDirection={['column']}>
                                        <Box width={[1, null, 1 / 3]}>
                                            {isFullDay ? (
                                                <FormattedMessage defaultMessage="Hele dag" />
                                            ) : (
                                                <>
                                                    <SlotDate>
                                                        <FormattedTime value={parseDateTime(slot.startDateTime)} /> —{' '}
                                                        <FormattedTime value={parseDateTime(slot.endDateTime)} />
                                                    </SlotDate>
                                                    {slot.label && (
                                                        <Box mt={4} style={{ maxWidth: '25rem' }}>
                                                            <TimeslotLabel>{slot.label}</TimeslotLabel>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                        <ActivityItemwrapper>
                                            <ActivityRateWrapper>
                                                {slot.rateGroup?.rates.map(rate => (
                                                    <Flex
                                                        key={rate.name}
                                                        justifyContent="space-between"
                                                        alignItems="end"
                                                    >
                                                        <Box>
                                                            <TicketBody>{rate.name}</TicketBody>
                                                        </Box>
                                                        <Box>
                                                            <TicketPrice>
                                                                {!rate.price && (
                                                                    <FormattedMessage defaultMessage="gratis" />
                                                                )}
                                                                {!!rate.price && (
                                                                    <FormattedNumber value={rate.price} format="EUR" />
                                                                )}
                                                            </TicketPrice>
                                                        </Box>
                                                    </Flex>
                                                ))}
                                            </ActivityRateWrapper>
                                            <BookButtonWrapper
                                                mt={[5, null, 0]}
                                                flex={1}
                                                hasAllotmentAvailable={(slot.allotment && slot.allotment < 10) ?? false}
                                            >
                                                {allotmentAvailable &&
                                                    slot.allotment &&
                                                    slot.allotment < 10 &&
                                                    activity.bookingType === ActivityBookingTypesEnum.Direct && (
                                                        <Label style={{ textAlign: 'end' }}>
                                                            <FormattedMessage
                                                                defaultMessage="Nog {allotment} beschikbaar"
                                                                values={{ allotment: slot.allotment ?? 0 }}
                                                            />
                                                        </Label>
                                                    )}
                                                <Flex
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                    justifyContent="flex-end"
                                                    my="auto"
                                                >
                                                    {bookButton}

                                                    {!bookButton && (
                                                        <>
                                                            {allotmentAvailable && timeslotHasExpired && (
                                                                <Body variant="small">
                                                                    <FormattedMessage defaultMessage="Verkoopperiode verlopen" />
                                                                </Body>
                                                            )}

                                                            {!allotmentAvailable &&
                                                                activity.bookingType ===
                                                                    ActivityBookingTypesEnum.Direct && (
                                                                    <Button disabled>
                                                                        <FormattedMessage defaultMessage="Uitverkocht" />
                                                                    </Button>
                                                                )}
                                                            {activity.bookingType !==
                                                                ActivityBookingTypesEnum.Direct && (
                                                                <Body variant="small">
                                                                    <FormattedMessage defaultMessage="Geen verkoop via ons" />
                                                                </Body>
                                                            )}
                                                        </>
                                                    )}
                                                </Flex>
                                            </BookButtonWrapper>
                                        </ActivityItemwrapper>
                                    </Flex>
                                </StyledSlotBox>
                            );
                        })}
            </StyledSlotBoxWrapper>
        </Container>
    );
};

const renderBookButton = (
    activity: ActivityPlannerActivityFragment,
    allotmentAvailable: boolean,
    timeslotHasExpired: boolean,
    setTimeslot: (slot: ActivityPlannerTimeslotFragment | null) => void,
    slot: ActivityPlannerTimeslotFragment,
    timeslotIsInCart: boolean,
    intl: IntlShape
) => {
    if (activity.bookingType === ActivityBookingTypesEnum.External && activity.externalBookingUrl) {
        return (
            <BookButton
                variant="primary"
                target="_blank"
                as="a"
                style={{ whiteSpace: 'nowrap' }}
                href={activity.externalBookingUrl}
            >
                <FormattedMessage defaultMessage="Koop tickets direct bij de aanbieder" />
            </BookButton>
        );
    }

    if (allotmentAvailable && !timeslotHasExpired && activity.bookingType === ActivityBookingTypesEnum.Direct) {
        return (
            <BookButton onClick={() => setTimeslot(slot)} {...(timeslotIsInCart && { variant: 'outline' })}>
                {timeslotIsInCart ? (
                    <FormattedMessage defaultMessage="Wijzig tickets" />
                ) : (
                    intl.formatMessage(messages.buyTickets)
                )}
            </BookButton>
        );
    }
    return null;
};

export default ActivityPlannerTimeslots;

const Container = styled.div`
    background: white;
    width: 100%;
    overflow: hidden;
    position: relative;
`;

const TimeslotLabel = styled(Body).attrs({ variant: 'small' })`
    color: ${({ theme }) => theme.colors.neutral[50]};
    font-weight: 300;
`;

const Label = styled(Body).attrs({ variant: 'tiny' })`
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2.4rem;
    margin-bottom: 0.4rem;
`;

const TicketBody = styled(Body)`
    font-weight: 400;
    font-size: 1.4rem;
    line-height: 2rem;
    margin-right: 0.4rem;

    @media screen and (max-width: 720px) {
        font-size: 1rem;
    }
`;

const StyledBox = styled(Box)`
    display: flex;
    justify-content: space-between;
    margin-bottom: 2.4rem;
`;

const DateBody = styled(Body)`
    font-size: 1.6rem;
    font-weight: 400;
`;

const StyledHeading = styled(Heading)`
    margin-bottom: 0;
`;

const StyledSlotBoxWrapper = styled(Box)`
    position: absolute;
    left: 0;
    top: 5rem;
    right: 0;
    bottom: 0;
    overflow: auto;
    height: 70%;

    @media screen and (max-width: 1024px) {
        top: 8rem;
    }

    @media screen and (max-width: 720px) {
        position: relative;
        top: 0;
    }
`;

const StyledSlotBox = styled(Box)`
    border: 0.1rem solid #e0e0e0;
    border-radius: 0.8rem;
    padding: 1.6rem;
    margin-bottom: 1.2rem;
`;

const SlotDate = styled(Box)`
    font-weight: 700;
    font-size: 1.6rem;
    line-height: 2.4rem;
`;

const ActivityItemwrapper = styled(Box)`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        flex-direction: column;
    }
`;

const ActivityRateWrapper = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    flex-flow: column;
    flex: 1;
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        align-self: flex-start;
        width: 100%;
    }
`;

const TicketPrice = styled(TicketBody)``;

const BookButtonWrapper = styled(Flex)<{ hasAllotmentAvailable: boolean | number }>`
    display: flex;
    flex-direction: column;

    @media screen and (max-width: 720px) {
        flex-direction: row;
        justify-content: ${p => (p.hasAllotmentAvailable ? 'space-between' : 'flex-end')};
    }
`;
